/* eslint-disable no-case-declarations */
import {
  AttributeFilter,
  Combiner,
  DataRecord,
  FilterEntry,
  Filters,
  getAttributeFilter,
  isNestedFilter,
  ProxyDataSource,
  Row,
} from '@cloudio-saas/datasource-types';
import {
  getFixedDate,
  getFixedRange,
} from 'components/SmartSearch/SearchUtils';
import { formatISO, parseISO } from 'date-fns';
import { isEmpty, isNotEmpty } from 'utils/isEmpty';

export type FilterFunctionType = (
  filters: Filters<unknown>,
  dataSource: ProxyDataSource,
  record: Row<DataRecord>,
) => boolean;
export const combinerMap: {
  [key in Combiner]: FilterFunctionType;
} = {
  anyOf: (filters, dataSource, record) => {
    for (let i = 0; i < filters.length; i++) {
      if (applyFilter(filters[i], dataSource, record)) {
        return true;
      }
    }
    return false;
  },
  allOf: (filters, dataSource, record) => {
    for (let i = 0; i < filters.length; i++) {
      if (!applyFilter(filters[i], dataSource, record)) {
        return false;
      }
    }
    return true;
  },
  noneOf: (filters, dataSource, record) => {
    for (let i = 0; i < filters.length; i++) {
      if (applyFilter(filters[i], dataSource, record)) {
        return false;
      }
    }
    return true;
  },
};

export const hasAll = (str1: string, strs: string[]) => {
  for (let i = 0; i < strs.length; i++) {
    if (!str1.includes(strs[i] as string)) {
      return false;
    }
  }
  return true;
};

export const hasAny = (str1: string, strs: string[]) => {
  for (let i = 0; i < strs.length; i++) {
    if (str1.includes(strs[i] as string)) {
      return true;
    }
  }
  return false;
};

export function applyFilters<T extends DataRecord>(
  filters: Filters<T>,
  combiner: Combiner,
  dataSource: ProxyDataSource,
  record: Row<T>,
): boolean {
  return combinerMap[combiner](filters, dataSource, record);
}

function applyFilter<T extends DataRecord>(
  filter: FilterEntry<T>,
  datasource: ProxyDataSource,
  record: Row<T>,
): boolean {
  let val = false;
  let range = [];
  if (isNestedFilter(filter)) {
    const combiner: Combiner = Object.keys(filter)[0] as Combiner;
    return applyFilters(
      filter[combiner] as Filters<T>,
      combiner,
      datasource,
      record,
    );
  }
  const af: AttributeFilter<T> = getAttributeFilter(filter, datasource);
  // eslint-disable-next-line prefer-const
  let { key, value } = af;
  if (isEmpty(record[key])) {
    return af.operator === 'empty' || af.operator === 'null';
  }

  switch (af.dataType) {
    case 'String':
      if (Array.isArray(value)) {
        value = value as string[];
      } else {
        value = value as string;
      }

      const recordValue = record[key] as string;
      switch (af.operator) {
        case 'empty':
          val = isEmpty(record[key]);
          break;
        case 'notEmpty':
          val = isNotEmpty(record[key]);
          break;
        case 'is':
          val = recordValue === value;
          break;
        case 'iIs':
          val = recordValue.toLowerCase() === (value as string).toLowerCase();
          break;
        case 'not':
          val = recordValue !== value;
          break;
        case 'iNot':
          val = recordValue.toLowerCase() !== (value as string).toLowerCase();
          break;
        case 'like':
          val = recordValue.includes(value as string);
          break;
        case 'iLike':
          val = recordValue
            .toLowerCase()
            .includes((value as string).toLowerCase());
          // console.log('iLike', recordValue, value);
          break;
        case 'nct':
          val = !recordValue.includes(value as string);
          break;
        case 'iNct':
          val = !recordValue
            .toLowerCase()
            .includes((value as string).toLowerCase());
          break;
        case 'sw':
          val = recordValue.startsWith(value as string);
          break;
        case 'iSw':
          val = recordValue
            .toLowerCase()
            .startsWith((value as string).toLowerCase());
          break;
        case 'ew':
          val = recordValue.endsWith(value as string);
          break;
        case 'iEw':
          val = recordValue
            .toLowerCase()
            .endsWith((value as string).toLowerCase());
          break;
        case 'in':
          val = (value as string[]).includes(recordValue);
          break;
        case 'iIn':
          val = (value as string[])
            .map((v) => v.toLowerCase())
            .includes(recordValue.toLowerCase());
          break;
        case 'nin':
          val = !(value as string[]).includes(recordValue);
          break;
        case 'iNin':
          val = !(value as string[])
            .map((v) => v.toLowerCase())
            .includes(recordValue.toLowerCase());
          break;
        case 'hasAll':
          val = hasAll(recordValue, value as string[]);
          break;
        case 'iHasAll':
          val = hasAll(
            recordValue.toLowerCase(),
            (value as string[]).map((v) => v.toLowerCase()),
          );
          break;
        case 'hasAny':
          val = hasAny(recordValue, value as string[]);
          break;
        case 'iHasAny':
          val = hasAny(
            recordValue.toLowerCase(),
            (value as string[]).map((v) => v.toLowerCase()),
          );
          break;
        default:
          throw Error('not handled');
      }
      break;
    case 'Number':
      switch (af.operator) {
        case 'null':
          val = isEmpty(record[key]);
          break;
        case 'notNull':
          val = isNotEmpty(record[key]);
          break;
        case 'eq':
          val = record[key] === value;
          break;
        case 'ne':
          val = record[key] !== value;
          break;
        case 'gt':
          val = (record[key] as number) > (value as number);
          break;
        case 'lt':
          val = (record[key] as number) < (value as number);
          break;
        case 'gte':
          val = (record[key] as number) >= (value as number);
          break;
        case 'lte':
          val = (record[key] as number) <= (value as number);
          break;
        case 'bn':
          val =
            (record[key] as number) >= (value as number[])?.[0] &&
            (record[key] as number) <= (value as number[])?.[1];
          break;
        case 'in':
          val = (value as number[]).includes(record[key] as number);
          break;
        case 'nin':
          val = !(value as number[]).includes(record[key] as number);
          break;
        default:
          throw Error('not handled');
      }
      break;
    case 'Date':
      const recordDate = parseISO(record[key] as string);
      switch (af.operator) {
        case 'empty':
          val = isEmpty(record[key]);
          break;
        case 'notEmpty':
          val = isNotEmpty(record[key]);
          break;
        case 'on':
          val =
            recordDate >=
              parseISO(formatISO(parseISO(String(value)).setHours(0, 0, 0))) &&
            recordDate <=
              parseISO(formatISO(parseISO(String(value)).setHours(23, 59, 59)));

          break;
        case 'notOn':
          val = !(
            recordDate >=
              parseISO(formatISO(parseISO(String(value)).setHours(0, 0, 0))) &&
            recordDate <=
              parseISO(formatISO(parseISO(String(value)).setHours(23, 59, 59)))
          );
          break;
        case 'after':
          val = recordDate > parseISO(String(value));
          break;
        case 'before':
          val = recordDate < parseISO(String(value));
          break;
        case 'onOrBefore':
          val = recordDate <= parseISO(String(value));
          break;
        case 'onOrAfter':
          val = recordDate >= parseISO(String(value));
          break;
        case 'bn':
          val =
            recordDate >= parseISO((value as string[])?.[0]) &&
            recordDate <= parseISO((value as string[])?.[1]);
          break;
        case 'today':
          val =
            recordDate >= parseISO(formatISO(new Date().setHours(0, 0, 0))) &&
            recordDate <= parseISO(formatISO(new Date().setHours(23, 59, 59)));
          break;
        case 'yesterday':
          let day = getFixedDate('yesterday') as Date;
          val =
            recordDate >= parseISO(formatISO(day.setHours(0, 0, 0))) &&
            recordDate <= parseISO(formatISO(day.setHours(23, 59, 59)));
          break;
        case 'tomorrow':
          day = getFixedDate('tomorrow') as Date;
          val =
            recordDate >= parseISO(formatISO(day.setHours(0, 0, 0))) &&
            recordDate <= parseISO(formatISO(day.setHours(23, 59, 59)));
          break;
        case 'last7days':
          range = getFixedRange('last7days');
          val =
            recordDate >= parseISO(formatISO(range[0] as Date)) &&
            recordDate <= parseISO(formatISO(range[1] as Date));
          break;
        case 'last14days':
          range = getFixedRange('last14days');
          val =
            recordDate >= parseISO(formatISO(range[0] as Date)) &&
            recordDate <= parseISO(formatISO(range[1] as Date));
          break;
        case 'last28days':
          range = getFixedRange('last28days');
          val =
            recordDate >= parseISO(formatISO(range[0] as Date)) &&
            recordDate <= parseISO(formatISO(range[1] as Date));
          break;
        case 'thisWeek':
          range = getFixedRange('thisWeek');
          val =
            recordDate >= parseISO(formatISO(range[0] as Date)) &&
            recordDate <= parseISO(formatISO(range[1] as Date));
          break;
        case 'thisMonth':
          range = getFixedRange('thisMonth');
          val =
            recordDate >= parseISO(formatISO(range[0] as Date)) &&
            recordDate <= parseISO(formatISO(range[1] as Date));
          break;
        case 'thisQuarter':
          range = getFixedRange('thisQuarter');
          val =
            recordDate >= parseISO(formatISO(range[0] as Date)) &&
            recordDate <= parseISO(formatISO(range[1] as Date));
          break;
        case 'thisYear':
          range = getFixedRange('thisYear');
          val =
            recordDate >= parseISO(formatISO(range[0] as Date)) &&
            recordDate <= parseISO(formatISO(range[1] as Date));
          break;

        default:
          throw Error('not handled');
      }
      break;
    case 'Boolean':
      switch (af.operator) {
        case 'empty':
          val = isEmpty(record[key]);
          break;
        case 'notEmpty':
          val = isNotEmpty(record[key]);
          break;
        case 'isTrue':
          val = record[key] === value;
          break;
        default:
          throw Error('not handled');
      }
      break;

    case 'YN':
    case 'TF':
      switch (af.operator) {
        case 'empty':
          val = isEmpty(record[key]);
          break;
        case 'notEmpty':
          val = isNotEmpty(record[key]);
          break;
        case 'is':
          val = record[key] === value;
          break;
        default:
          throw Error('not handled');
      }
      break;

    default:
      throw Error('not handled');
  }
  return val;
}
